import React, { useEffect, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useSubscription } from "@/hooks/useSubscription";
import { NavigationProvider } from "@/contexts/NavigationContext";
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";
import LandingPage from "./pages/LandingPage";
import Pricing from "./pages/Pricing";
import CGU from "./pages/CGU";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import About from "./pages/About";
import Confidentialite from "./pages/Confidentialite";
import MentionsLegales from "./pages/MentionsLegales";
import Securite from "./pages/Securite";
import AuthCallback from "./pages/AuthCallback";
import FeedbackButton from "./components/FeedbackButton";
import CookieBanner from "./components/CookieBanner";
import { PsyGestionSpinner } from "@/components/PsyGestionLogo";

const queryClient = new QueryClient();

// ─── ProtectedRoute ───────────────────────────────────────────────────────────
// Post-trial : accès au plan Découverte (10 recettes/mois) sans blocage dur.
// La bannière post-trial et le modal d'upgrade sont dans FoundingBanner.
// ─────────────────────────────────────────────────────────────────────────────
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const { loading: subLoading, refetch } = useSubscription();
  const toastShown = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "true" && !toastShown.current) {
      toastShown.current = true;
      window.history.replaceState({}, "", window.location.pathname);
      refetch();
      import("@/hooks/use-toast").then(({ toast }) => {
        toast({
          title: "Bienvenue !",
          description: "Votre abonnement Pro est en cours d'activation.",
        });
      });
    }
  }, [refetch]);

  if (loading || profileLoading || subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F4F8FC" }}>
        <PsyGestionSpinner size={56} />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  if (!profile?.has_completed_onboarding) return <Navigate to="/onboarding" replace />;

  return <NavigationProvider userId={user.id}>{children}</NavigationProvider>;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F4F8FC" }}>
      <PsyGestionSpinner size={56} />
    </div>
  );
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();

  if (loading || profileLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  // Ne rediriger vers /dashboard que si l'onboarding est explicitement terminé
  if (profile?.has_completed_onboarding) return <Navigate to="/dashboard" replace />;
  return <Onboarding />;
}

function AuthRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Auth />;
}

function HomeRoute() {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();

  // Attendre que auth ET profil soient résolus avant de décider
  // (évite le double-saut / → /dashboard → /onboarding et le flash d'écran)
  if (authLoading || (user && profileLoading)) return <LoadingScreen />;

  // Visiteur anonyme → LandingPage (comportement public par défaut)
  if (!user) return <LandingPage />;

  // Connecté sans onboarding → /onboarding (direct, sans passer par /dashboard)
  if (!profile?.has_completed_onboarding) return <Navigate to="/onboarding" replace />;

  // Connecté + onboarding terminé → /dashboard
  return <Navigate to="/dashboard" replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/auth" element={<AuthRoute />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/securite" element={<Securite />} />
          {/* Alias routes pour les conventions françaises SEO */}
          <Route path="/cgu-cgv" element={<Navigate to="/cgu" replace />} />
          <Route path="/politique-confidentialite" element={<Navigate to="/confidentialite" replace />} />
          <Route path="/onboarding" element={<OnboardingRoute />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/profil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile" element={<Navigate to="/profil" replace />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <FeedbackButton />
        <CookieBanner />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
